/* eslint-disable react/prop-types */
import { createUseStyles, useTheme } from '@glu/theming';
import { createPortal } from 'react-dom';
import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { useTrapFocus, debounce, useClickOutside } from '@glu/utilities-react';
import classnames from 'classnames';
import RangeLayout from './RangeLayout';
import DateSelect from './DateSelect';
import styles from './Dropdown.styles';

const useStyles = createUseStyles(styles);

const Dropdown = (props) => {
  const {
    alwaysAlignRight,
    alwaysOpenUp,
    clickOutsideToClose,
    dropdownClassName,
    error,
    errors,
    fieldRef,
    forcePositionX,
    forcePositionY,
    htmlId,
    inputRef,
    labelText,
    onBlur,
    onClose,
    portalPosition = 'bottom',
    ranges,
    screenReaderLabel,
    usePortal,
  } = props;
  const classes = useStyles();
  const popupRef = useRef();
  const wrappingRef = useRef();
  const { datepickerReact } = useTheme();

  const [alignRight, setAlignRight] = useState(alwaysAlignRight);
  const [onTop, setOnTop] = useState(false);
  const [positionLeft, setPositionLeft] = useState();
  const [positionRight, setPositionRight] = useState();
  const [positionTop, setPositionTop] = useState();
  const [positionBottom, setPositionBottom] = useState();
  const [editing, setEditing] = useState(false);

  useLayoutEffect(() => {
    if (!usePortal) {
      const {
        height,
        left: popupLeft,
        right,
        top: popupTop,
        width,
      } = popupRef.current.getBoundingClientRect();

      const { innerHeight, innerWidth } = window;
      const overlapsBottomEdge =
        popupTop + height > innerHeight || popupTop > innerHeight - popupTop;
      const overflowsRightEdge = popupLeft > innerWidth - right || innerWidth - right < width;
      const overlapsTop =
        height + datepickerReact.inputHeight * 2 + datepickerReact.chevronPointsDown.marginBottom >
        popupTop;

      /* istanbul ignore next */
      if (overflowsRightEdge && !forcePositionX) {
        setAlignRight(true);
      }

      /* istanbul ignore next */
      if (((overlapsBottomEdge && !overlapsTop) || alwaysOpenUp) && !forcePositionY) {
        setOnTop(true);
      }
    }
  }, [
    alwaysAlignRight,
    usePortal,
    alwaysOpenUp,
    forcePositionX,
    forcePositionY,
    datepickerReact.inputHeight,
    datepickerReact.chevronPointsDown.marginBottom,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resetDropdownPosition = useCallback(() => {
    if (usePortal && fieldRef.current?.querySelector) {
      const field = fieldRef.current.getBoundingClientRect();
      const popup = popupRef.current.getBoundingClientRect();
      const { innerHeight, innerWidth } = window;
      const overflowsRightEdge = field.x + (popup.right - popup.left) > innerWidth;

      if (overflowsRightEdge) {
        setAlignRight(true);
        setPositionLeft('auto');
        setPositionRight(innerWidth - (field.left + field.width));
      } else {
        setPositionLeft(field.x);
        setPositionRight('auto');
        setAlignRight(false);
      }
      if (portalPosition === 'top') {
        setOnTop(true);
        setPositionBottom(innerHeight - field.y);
      } else {
        setPositionTop(field.y + field.height);
      }
    }
  }, [usePortal, fieldRef, portalPosition]);

  useEffect(() => {
    resetDropdownPosition();
    const debouncedResetDropdownPosition = debounce(resetDropdownPosition, 100);

    window.addEventListener('scroll', debouncedResetDropdownPosition);
    window.addEventListener('resize', debouncedResetDropdownPosition);
    return () => {
      window.removeEventListener('scroll', debouncedResetDropdownPosition);
      window.removeEventListener('resize', debouncedResetDropdownPosition);
    };
  }, [usePortal, fieldRef, resetDropdownPosition, setPositionLeft, setPositionTop]);

  const handleSetEditing = (value) => {
    setEditing(value);
    resetDropdownPosition();
  };

  const handleOverLayClick = () => {
    if (clickOutsideToClose) {
      onClose();
      onBlur();
    }
  };

  useClickOutside(fieldRef, handleOverLayClick);

  const handleTrapFocus = useTrapFocus({ active: true, ref: wrappingRef });

  const onKeyDown = useCallback(
    (e) => {
      if (e.which === 27) {
        /** close on escape key */
        onClose();
        inputRef.current.focus();
      } else {
        /** otherwise trap the focus in the dropdown */
        handleTrapFocus(e);
      }
    },
    [handleTrapFocus, onClose]
  );

  /* istanbul ignore next */
  const Popup = (
    <div
      ref={wrappingRef}
      className={classnames(classes.overlay, dropdownClassName, {
        [classes.clickOutsideToClose]: clickOutsideToClose,
        [classes.usePortal]: usePortal,
      })}
      onKeyDown={onKeyDown}
      role="presentation"
    >
      <div
        ref={popupRef}
        className={classnames(
          classes.popup,
          onTop ? classes.onTop : classes.onBottom,
          alignRight ? classes.alignRight : classes.alignLeft,
          {
            [classes.labelText]: !!labelText && !screenReaderLabel,
            [classes.screenReaderLabel]: screenReaderLabel,
            [classes.usePortal]: usePortal,
          },
          { [classes.onTopError]: (error || errors?.[0]) && onTop }
        )}
        data-qa="popup"
        id={`${htmlId}-popup`}
        style={{
          bottom: onTop ? positionBottom : undefined,
          left: positionLeft,
          position: usePortal ? 'absolute' : undefined,
          right: positionRight,
          top: onTop ? undefined : positionTop,
        }}
      >
        {ranges ? (
          <RangeLayout
            {...props}
            alignRight={alignRight}
            editing={editing}
            error={error || errors?.[0]}
            onTop={onTop}
            setEditing={handleSetEditing}
          />
        ) : (
          <DateSelect {...props} />
        )}
      </div>
    </div>
  );

  return usePortal ? createPortal(Popup, document.querySelector('body')) : Popup;
};

export default Dropdown;
